/**
 * Chart.js defaults for the slate console. Not the old teal SVG charts.
 * Register scales/elements once. Trunk ids are never renamed.
 */

import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  type ChartOptions,
  type TooltipItem,
} from "chart.js";

import { sipDisplayName } from "../lib/sipLabels";

export const INK = "#e6e8eb";
export const INK2 = "#a4abb4";
export const MUTE = "#6f7782";
export const SURFACE = "#14171b";
export const RAISED = "#1b1f24";
export const BORDER = "#2a3038";
export const HAIRLINE = "rgba(255, 255, 255, 0.06)";
export const ACCENT = "#7c8cf8";

const SANS = '"IBM Plex Sans", ui-sans-serif, system-ui, sans-serif';
const MONO = '"IBM Plex Mono", ui-monospace, SFMono-Regular, monospace';

/** sofia id → stable color. Unknown ids use mute. */
export const TRUNK_COLOR: Record<string, string> = {
  cd: "#7c8cf8",
  bitcall: "#5fc88f",
  voxa: "#d8a25c",
  wavoip: "#95a3ff",
  in287: "#e57584",
};

export const AMD_COLOR = {
  HUMAN: "#5fc88f",
  MACHINE: "#e57584",
  SCREENING: "#d8a25c",
  UNKNOWN: "#6f7782",
} as const;

export const SOFIA_STATES = ["REGED", "NOREG", "UP", "FAIL_WAIT", "DOWN", "UNKNOWN", "OFFLINE"] as const;
export type SofiaState = (typeof SOFIA_STATES)[number];

export const STATE_COLOR: Record<SofiaState, string> = {
  REGED: "#5fc88f",
  NOREG: "#95a3ff",
  UP: "#5fc88f",
  FAIL_WAIT: "#d8a25c",
  DOWN: "#e57584",
  UNKNOWN: "#6f7782",
  OFFLINE: "#6f7782",
};

const MIX_PALETTE = ["#7c8cf8", "#5fc88f", "#d8a25c", "#e57584", "#95a3ff", "#a4abb4", "#6f7782", "#3a414b"];

let registered = false;

export function registerCharts(): void {
  if (registered) return;
  Chart.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    ArcElement,
    Tooltip,
    Legend,
    Filler,
  );
  Chart.defaults.color = INK2;
  Chart.defaults.borderColor = HAIRLINE;
  Chart.defaults.font.family = SANS;
  Chart.defaults.font.size = 12;
  Chart.defaults.responsive = true;
  Chart.defaults.maintainAspectRatio = false;
  Chart.defaults.animation = { duration: 360 };
  Chart.defaults.plugins.legend.labels.color = INK2;
  Chart.defaults.plugins.legend.labels.font = { family: SANS, size: 12 };
  Chart.defaults.plugins.tooltip.backgroundColor = RAISED;
  Chart.defaults.plugins.tooltip.borderColor = BORDER;
  Chart.defaults.plugins.tooltip.borderWidth = 1;
  Chart.defaults.plugins.tooltip.titleColor = INK;
  Chart.defaults.plugins.tooltip.bodyColor = INK2;
  Chart.defaults.plugins.tooltip.titleFont = { family: SANS, size: 12 };
  Chart.defaults.plugins.tooltip.bodyFont = { family: SANS, size: 12 };
  registered = true;
}

registerCharts();

function withAlpha(hex: string, alpha: number): string {
  const h = hex.replace("#", "");
  if (h.length !== 6) return hex;
  const r = Number.parseInt(h.slice(0, 2), 16);
  const g = Number.parseInt(h.slice(2, 4), 16);
  const b = Number.parseInt(h.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return hex;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function softColor(hex: string, alpha = 0.16): string {
  return withAlpha(hex, alpha);
}

export function trunkColor(trunkId: string, muted = false): string {
  const id = (trunkId || "").trim().toLowerCase();
  const hex = TRUNK_COLOR[id] ?? MUTE;
  return muted ? withAlpha(hex, 0.45) : hex;
}

/** Chart axis label. Sofia id is unchanged; this is display text only. */
export function trunkChartLabel(trunkId: string, storedName?: string | null): string {
  const id = (trunkId || "").trim() || "unknown";
  return sipDisplayName(id, storedName || undefined);
}

export function amdColor(label: string): string | null {
  const key = label.trim().toUpperCase();
  if (key === "HUMAN") return AMD_COLOR.HUMAN;
  if (key === "MACHINE" || key === "VM" || key === "VOICEMAIL") return AMD_COLOR.MACHINE;
  if (key === "SCREENING") return AMD_COLOR.SCREENING;
  if (key === "UNKNOWN") return AMD_COLOR.UNKNOWN;
  return null;
}

export function sliceColor(label: string, index: number): string {
  return amdColor(label) ?? MIX_PALETTE[index % MIX_PALETTE.length] ?? MUTE;
}

export function sofiaStateBucket(raw: string | null | undefined): SofiaState {
  const value = (raw ?? "").trim().toUpperCase().replace(/[\s-]+/g, "_");
  if ((SOFIA_STATES as readonly string[]).includes(value)) return value as SofiaState;
  return "UNKNOWN";
}

function tickFont() {
  return { family: MONO, size: 11 };
}

function categoryScale() {
  return {
    ticks: { color: INK2, font: tickFont(), autoSkip: false, maxRotation: 0, minRotation: 0 },
    grid: { display: false },
    border: { color: BORDER },
  };
}

function valueScale(percent: boolean, max: number | undefined) {
  return {
    beginAtZero: true,
    ...(percent ? { max: Math.max(100, max ?? 100) } : {}),
    ticks: {
      color: INK2,
      font: tickFont(),
      ...(percent ? { callback: (value: string | number) => `${value}%` } : {}),
    },
    grid: { color: HAIRLINE },
    border: { color: BORDER },
  };
}

function legend(display: boolean) {
  return {
    display,
    position: "bottom" as const,
    labels: {
      color: INK2,
      font: { family: SANS, size: 12 },
      boxWidth: 8,
      boxHeight: 8,
      usePointStyle: true,
      pointStyle: "circle" as const,
    },
  };
}

function tooltip() {
  return {
    backgroundColor: RAISED,
    borderColor: BORDER,
    borderWidth: 1,
    titleColor: INK,
    bodyColor: INK2,
    titleFont: { family: SANS, size: 12 },
    bodyFont: { family: SANS, size: 12 },
    padding: 10,
    displayColors: true,
  };
}

export function lineOptions(): ChartOptions<"line"> {
  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 360 },
    color: INK2,
    font: { family: SANS, size: 12 },
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: legend(true),
      tooltip: tooltip(),
    },
    scales: {
      x: categoryScale(),
      y: { ...valueScale(false, undefined), beginAtZero: true },
    },
  };
}

export function barOptions(input: {
  horizontal?: boolean;
  percent?: boolean;
  legend?: boolean;
  max?: number;
}): ChartOptions<"bar"> {
  const horizontal = Boolean(input.horizontal);
  const percent = Boolean(input.percent);
  const value = valueScale(percent, input.max);
  const category = categoryScale();
  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 360 },
    indexAxis: horizontal ? "y" : "x",
    color: INK2,
    font: { family: SANS, size: 12 },
    interaction: { mode: "nearest", intersect: false },
    plugins: {
      legend: legend(input.legend !== false),
      tooltip: {
        ...tooltip(),
        callbacks: percent
          ? {
              label(item: TooltipItem<"bar">) {
                const parsed = item.parsed;
                const raw = horizontal ? parsed.x : parsed.y;
                const shown = typeof raw === "number" && Number.isFinite(raw) ? `${raw.toFixed(1)}%` : "—";
                return `${item.dataset.label ?? "value"}: ${shown}`;
              },
            }
          : undefined,
      },
    },
    scales: horizontal
      ? { x: value, y: category }
      : { x: category, y: value },
  };
}

export function doughnutOptions(showLegend = true): ChartOptions<"doughnut"> {
  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 360 },
    cutout: "68%",
    color: INK2,
    font: { family: SANS, size: 12 },
    plugins: {
      legend: legend(showLegend),
      tooltip: tooltip(),
    },
  };
}
